/**
 * Feedback Policy (TV1-2417)
 *
 * Forwards a seller's thumbs up/down on an assistant answer to the backend, where it
 * becomes a Langfuse `user-feedback` score on THAT answer's trace.
 *
 * ## Why a policy and not a call in the click handler
 *
 * Same reason every other backend touch in this domain is one: the component dispatches
 * a command describing what the seller did, and the effect lives outside React. It also
 * means the optimistic rating (`handleFeedbackSubmit`) and the network write cannot get
 * out of order — the state is already committed by the time this runs.
 *
 * ## Why a state DIFF and not the command
 *
 * The CDSE `Policy` surface is `shouldReact( prev, next )` + `onStateChange` — there is
 * no command hook. That is not a compromise here: `feedback` is set only by
 * `feedback/submit`, and re-selecting the rating a message already has produces an
 * identical state, so the diff fires exactly once per genuine rating and never for a
 * no-op re-click. The comparison is per-message so a rating cannot be attributed to the
 * wrong bubble.
 *
 * ## THIS POLICY CANNOT FAIL THE USER
 *
 * It dispatches nothing back. No rollback, no error command, no toast. A rating that
 * silently did not reach the sink looks exactly like one that did, and that is the
 * intended behaviour, not an omission:
 *
 *  - Langfuse is configured in NO deployed environment, so today the endpoint answers
 *    200 `{ recorded: false }` for every click. A UI that surfaced that would tell every
 *    seller her feedback failed;
 *  - most answers carry no `traceId` at all (it exists only under a recording tracer
 *    provider), so there is frequently nothing to send in the first place;
 *  - and the rating this feature most needs is the NEGATIVE one. Anything that makes a
 *    thumbs-down feel like it broke something trains the seller out of giving it.
 *
 * The honest signal for an operator is the `recorded` flag on the response and the
 * server-side `langfuse.score.*` warn logs — not the seller's screen.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { AIAssistantState } from '../state';
import { AIAssistantCommand } from '../commands';
import { AIAssistantService } from '@infrastructure/aiAssistant/aiAssistantService';

/** Messages whose `feedback` changed between two states — i.e. just-given ratings. */
const newlyRated = ( prev: AIAssistantState, next: AIAssistantState ) => {
  const before = new Map( prev.messages.map( m => [ m.id, m.feedback ]));
  return next.messages.filter( m => m.feedback && before.get( m.id ) !== m.feedback );
};

export const createFeedbackPolicy = (
  service: AIAssistantService
): Policy<AIAssistantState, AIAssistantCommand> => ({
  name: 'ai-feedback',

  shouldReact: ( prev, next ) => newlyRated( prev, next ).length > 0,

  onStateChange: ({ prev, next }) => {
    for ( const message of newlyRated( prev, next )) {
      // No trace, nothing to score. The rating still shows as given — see above.
      if ( !message.traceId ) continue;
      void service.sendFeedback({ traceId: message.traceId, rating: message.feedback! });
    }
  }
});
